import { ChevronDown, History, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Fragment, useCallback, useMemo, useState } from 'react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

export type HistoryCollapsibleProps<T> = {
  /** トリガーに表示するラベル */
  label: string
  /** データ取得関数（初回オープン時に1回だけ呼ばれる） */
  fetchItems: () => Promise<T[]>
  /** アイテムから日付グループキー（YYYY-MM-DD等）を抽出 */
  getDateKey: (item: T) => string
  /** 日付グループヘッダーの表示文字列 */
  formatDateHeader: (dateKey: string) => string
  /** アイテムのユニークキー */
  getItemKey: (item: T) => string | number
  /** 各アイテムの描画 */
  renderItem: (item: T) => ReactNode
  /** リスト末尾に表示するフッター（合計表示等） */
  renderFooter?: (items: T[]) => ReactNode
  /** データ0件時のメッセージ */
  emptyMessage?: string
  /** エラー時コールバック */
  onError?: () => void
}

/** 履歴系Collapsibleの共通コンポーネント（遅延読み込み + 日付グループ化） */
export const HistoryCollapsible = <T,>({
  label,
  fetchItems,
  getDateKey,
  formatDateHeader,
  getItemKey,
  renderItem,
  renderFooter,
  emptyMessage = '履歴がありません',
  onError,
}: HistoryCollapsibleProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [items, setItems] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      setIsOpen(open)
      if (open && !hasFetched) {
        setIsLoading(true)
        try {
          const result = await fetchItems()
          setItems(result)
          setHasFetched(true)
        } catch {
          onError?.()
        } finally {
          setIsLoading(false)
        }
      }
    },
    [fetchItems, hasFetched, onError],
  )

  // 日付ごとにグループ化（items が変わらない限りスキップ）
  const grouped = useMemo(() => {
    const map = new Map<string, T[]>()
    for (const item of items) {
      const dateKey = getDateKey(item)
      const list = map.get(dateKey) ?? []
      list.push(item)
      map.set(dateKey, list)
    }
    return map
  }, [items, getDateKey])

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
        <History className="size-3.5" />
        <span>{label}</span>
        <ChevronDown className={`size-3.5 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
            {Array.from(grouped.entries()).map(([dateKey, groupItems]) => (
              <div key={dateKey} className="space-y-0.5">
                <p className="text-[11px] font-medium text-muted-foreground px-1 sticky top-0 bg-background/90 backdrop-blur-sm py-0.5">
                  {formatDateHeader(dateKey)}
                </p>
                {groupItems.map((item) => (
                  <Fragment key={getItemKey(item)}>{renderItem(item)}</Fragment>
                ))}
              </div>
            ))}
            {items.length === 0 && !isLoading && (
              <p className="text-xs text-muted-foreground text-center py-2">{emptyMessage}</p>
            )}
            {items.length > 0 && renderFooter?.(items)}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
